import React, { useState, useEffect } from "react";
import { getWithExpiry, setWithExpiry } from "../../common";
import configVars from "../../config";
import Alert from "./../Alert";
import { useTranslation } from "react-i18next";
import validator from "validator";
import { useCookies } from "react-cookie";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const AddNewUser = (props) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [organization, setOrganization] = useState(0);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [userMembership, setUserMembership] = useState(0);
  const [skipOnboarding, setSkipOnboarding] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [translation] = useTranslation("translations");
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [cookies] = useCookies(["sustainyou_user_jwt"]);

  const [startDate, setStartDate] = useState( new Date() );
  const [endDate, setEndDate] = useState( new Date() );
  const [customDates, setCustomDates] = useState(false);

  useEffect(() => {
    getOrganizations();
  }, []);

  const handleAddUser = async (event) => {
    event.preventDefault();

    setIsError(false);
    setIsSuccess(false);
    setErrorMsg("");
    setSuccessMsg("");

    const validation = handleValidation(
      firstName,
      lastName,
      userEmail,
      organization,
      userMembership,
      userType
    );

    if (!validation.isValidated) {
      setIsError(true);
      setErrorMsg(validation.validationMsg);
      return;
    }

    setSavingUser(true);

    let userRequestData = new FormData();
        userRequestData.append( 'token', cookies.sustainyou_user_jwt );
        userRequestData.append( 'organization_id', organization );
        userRequestData.append( 'first_name', firstName );
        userRequestData.append( 'last_name', lastName );
        userRequestData.append( 'email', userEmail );
        userRequestData.append( 'membership', userMembership );
        userRequestData.append( 'user_type', userType );
        userRequestData.append( 'skiponb', skipOnboarding );

        if ( customDates ) {

          if ( startDate ) {
            userRequestData.append( 'start_date', moment(startDate).format("YYYY-MM-DD") );
          }

          if ( endDate ) {
            userRequestData.append( 'end_date', moment(endDate).format("YYYY-MM-DD") );
          }
        }

    const response = await fetch(
      `${configVars.apiUrl}sustainchange/v1/addnewsyuser/`,
      {
        method: "POST",
        headers: {
          Authorization: `Basic ${configVars.auth}`,
        },
        body: userRequestData
      }
    );

    const memberResponse = await response.json();

    if (memberResponse) {

      if (memberResponse.success === true) {
        setIsSuccess(true);
        setSuccessMsg("User added successfully.");
        setFirstName("");
        setLastName("");
        setUserEmail("");
        setUserType("");
        setOrganization(0);
        setUserMembership(0);
      } else {
        setIsError(true);
        setErrorMsg(memberResponse.message);
      }

      setSavingUser(false);
    }
  };

  const handleValidation = (
    firstName,
    lastName,
    userEmail,
    organization,
    userMembership,
    userType
  ) => {
    let isValidated = true;
    let validationMsg = "";

    if (
      !firstName.toString().trim().length ||
      !lastName.toString().trim().length ||
      !userEmail.toString().trim().length ||
      !userType.toString().trim().length
    ) {
      isValidated = false;
      validationMsg = translation("fill_required_fields");
    }

    if (!validator.isEmail(userEmail)) {
      isValidated = false;
      validationMsg = translation("enter_valid_email");
    }

    if (!userMembership && userMembership === 0) {
      isValidated = false;
      validationMsg = "Please select user membership plan";
    }

    return {
      isValidated: isValidated,
      validationMsg: validationMsg,
    };
  };

  const getOrganizations = async () => {
    const savedOrganizations = getWithExpiry("sy_all_organizations");

    if (savedOrganizations) {
      setAllOrganizations(savedOrganizations);
    } else {
      setLoadingOrganizations(true);

      const response = await fetch(
        `${configVars.apiUrl}sustainchange/v1/getorganizations`,
        {
          method: "GET",
          headers: {
            Authorization: `Basic ${configVars.auth}`,
          },
        }
      );

      const companyResponse = await response.json();

      if (companyResponse) {
        if (companyResponse.success === true) {
          setAllOrganizations(companyResponse.data);
          setWithExpiry("sy_all_organizations", companyResponse.data);
        }
      }

      setLoadingOrganizations(false);
    }
  };

  return (
    <div className="add-new-user-wrap">
      <h3 className="mb-4">Add new user</h3>
      <form onSubmit={(event) => handleAddUser(event)}>
        {isError && (
          <div className="mt-3" style={{ maxWidth: "800px" }}>
            <Alert type="danger" msg={errorMsg} />
          </div>
        )}

        {isSuccess && (
          <div className="mt-3" style={{ maxWidth: "800px" }}>
            <Alert type="success" msg={successMsg} />
          </div>
        )}

        <div className="row mb-3 user-row">
          <div className="col-12 col-lg-6">
            <input
              type="text"
              className="form-control"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="col-12 col-lg-6">
            <input
              type="text"
              className="form-control"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div className="row mb-3 user-row">
          <div className="col-12 col-lg-6">
            <input
              type="email"
              className="form-control"
              placeholder="Email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
          </div>
          <div className="col-12 col-lg-6">
            <select
              className="form-select"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <option value="">Select user type</option>
              <option value="sustainchange_owner">Owner</option>
              <option value="sustainchange_manager">Manager</option>
              <option value="subscriber">Normal</option>
            </select>
          </div>
        </div>

        <div className="row mb-3 user-row">
          <div className="col-12 col-lg-6">
            <div className="row">
              <div className="col">
                <select
                  className="form-select"
                  aria-describedby="companyHelp"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                >
                  {loadingOrganizations && (
                    <option value="0">Loading organizations...</option>
                  )}
                  {!loadingOrganizations && (
                    <option value="0">Select user organization</option>
                  )}
                  {allOrganizations &&
                    allOrganizations.length > 0 &&
                    allOrganizations.map((organization, index) => {
                      return (
                        <option value={organization.id} key={index}>
                          {organization.name}
                        </option>
                      );
                    })}
                </select>
              </div>

              {loadingOrganizations && (
                <div className="col-auto">
                  <div
                    className="spinner-border text-primary spinner-border-sm mt-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className="row">
              <div className="col">
                <select
                  className="form-select"
                  aria-describedby="companyHelp"
                  value={userMembership}
                  onChange={(e) => setUserMembership(e.target.value)}
                >
                  {props?.loadingMemberships && (
                    <option value="0">Loading memberships...</option>
                  )}
                  {!props?.loadingMemberships && (
                    <option value="0">Select user membersip plan</option>
                  )}

                  {props?.allMemberships &&
                    props?.allMemberships.length > 0 &&
                    props?.allMemberships.map((membership, index) => {
                      return (
                        <option value={membership.id} key={index}>
                          {membership.name}
                        </option>
                      );
                    })}
                </select>
              </div>
              {props?.loadingMemberships && (
                <div className="col-auto">
                  <div
                    className="spinner-border text-primary spinner-border-sm mt-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row mb-3 user-row">
          <div className="col-12">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="hasCustomDates"
                onChange={(e) => setCustomDates(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="hasCustomDates">
                Custom dates
              </label>
            </div>
          </div>
        </div>

        {customDates &&
          <div className="row mb-3 user-row">
            <div className="col-12 col-lg-6">
              <label htmlFor="start_date" className="form-label">Start date</label>
              <DatePicker className="form-control" selected={startDate} onChange={(date) => { setStartDate(date);setEndDate(date)}} dateFormat="yyyy-MM-dd" name="start_date" isClearable={true} />
            </div>
            <div className="col-12 col-lg-6">
              <label htmlFor="end_date" className="form-label">End date</label>
              <DatePicker className="form-control" selected={endDate} onChange={(date) => setEndDate(date)} dateFormat="yyyy-MM-dd" minDate={startDate} name="end_date" isClearable={true} />
            </div>
          </div>
        }

        <div className="row mb-3 user-row">
          <div className="col-12">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="skipOnboarding"
                onChange={(e) => setSkipOnboarding(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="skipOnboarding">
                Skip Onboarding
              </label>
            </div>
          </div>
        </div>

        <div className="row mb-3 user-row">
          <div className="col-12">
            <button type="submit" className="btn btn-primary text-white">
              {!savingUser && <i className="fa fa-user" aria-hidden="true"></i>}
              {savingUser && (
                <div
                  className="spinner-border text-white spinner-border-sm"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
              <span className="ms-3">Add User</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddNewUser;
